import React, { useState } from 'react';
import TimelineCalendar, {
	TimelineCalendarEvent,
} from '../TimelineCalendar/TimelineCalendar';
import {
	ServiceSchedule,
	ServiceScheduleItem,
	ServiceScheduleType,
} from '../../../../graphql/graphql';
import useServiceSchedules from '../../../Workplace/Hooks/useServiceSchedules';
import { getMomentDate } from '../../../../Utils/dates.util';
import moment, { Moment } from 'moment';
import { useEffectAsync } from '../../../../helpers';
import OverlayLoading from '../../../../Components/OverlayLoading';
import ContextMenu, {
	ContextMenuParams,
} from '../../../../Components/ContextMenu/ContextMenu';
import ServiceScheduleCreateModal from '../../../ServiceSchedule/Components/ServiceScheduleCreate/ServiceScheduleCreateModal';
import { useServiceScheduleContext } from '../../../WorkplaceMember/Services/ServiceScheduleContext';
import ServiceScheduleUpdateModal from '../../../ServiceSchedule/Components/ServiceScheduleUpdate/ServiceScheduleUpdateModal';
import { EventClickArg, EventContentArg } from '@fullcalendar/react';
import ConfirmationModal from '../../../Base/Components/Confirmation/ConfirmationModal';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import useTranslation from '../../../../Hooks/useTranslation';
import apiClient from '../../../../Services/ApiClient';
import LinkButton from '../../../../Components/Button/LinkButton';
import { CalendarView } from '../../Enums/CalendarView.eunm';
import Tooltip from '../../../../Components/Popup/Tooltip';
import { getShouldBeHintDisplayed } from '../../../Manager/Services/manager.service';
import OnlineOrderingNotificationModal from '../../../Manager/Components/ServiceSchedules/OnlineOrderingNotitication/OnlineOrderingNotificationModal';
import ServiceScheduleInstructionsModal from '../../../Manager/Components/ServiceSchedules/ServiceScheduleInstructions/ServiceScheduleInstructionsModal';
import Loading from '../../../../Components/Loading';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useRestApiClient from '../../../Base/Hooks/useRestApiClient';

interface Props {
	scheduleTypes: ServiceScheduleType[];
	workplaceId: string;
	showNotification?: boolean;
}

const ServiceScheduleCalendar: React.FC<Props> = ({
	workplaceId,
	showNotification,
	scheduleTypes,
}) => {
	const { timelineCalendarResources, workplaceName } =
		useServiceScheduleContext();
	const { t } = useTranslation();
	const {
		getSchedulesAsTimeline,
		getAbsenceEventsAsTimeline,
		isLoading: isLoadingEvents,
	} = useServiceSchedules();
	const [events, setEvents] = useState<TimelineCalendarEvent[]>([]);
	const [dateRange, setDateRange] = useState<
		{ start: Moment; end: Moment } | undefined
	>(undefined);
	const [currentCalendarView, setCurrentCalendarView] = useState<
		CalendarView | undefined
	>(undefined);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
	const [contextMenuProps, setContextMenuProps] = useState<ContextMenuParams>({
		isOpen: false,
	});
	const [selectedDate, setSelectedDate] = useState<Moment | undefined>(
		moment(),
	);
	const [selectedResourceId, setSelectedResourceId] = useState<
		string | undefined
	>(undefined);
	const [selectedServiceSchedule, setSelectedServiceSchedule] = useState<
		ServiceSchedule | undefined
	>(undefined);
	const [selectedServiceScheduleItem, setSelectedServiceScheduleItem] =
		useState<ServiceScheduleItem | undefined>(undefined);
	const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
		useState(false);
	const [
		isMultipleDeleteConfirmationOpen,
		setIsMultipleDeleteConfirmationOpen,
	] = useState(false);
	const [
		isOnlineOrderingNotificationOpen,
		setIsOnlineOrderingNotificationOpen,
	] = useState(false);
	const [isInstructionModalOpen, setIsInstructionModalOpen] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingPrint, setIsLoadingPrint] = useState(false);
	const { displayError } = useApiClient();
	const { showUnexpectedError } = useRestApiClient();

	const handleLoadSchedule = async () => {
		const tempEvents: TimelineCalendarEvent[] = [];
		if (dateRange) {
			const shiftEvents = await getSchedulesAsTimeline({
				from: getMomentDate(dateRange.start),
				to: getMomentDate(dateRange.end),
				workplaceIds: [workplaceId],
				types: scheduleTypes,
				calendarView: currentCalendarView,
			});
			tempEvents.push(...shiftEvents);
			const absenceEvents = await getAbsenceEventsAsTimeline({
				from: getMomentDate(dateRange.start),
				to: getMomentDate(dateRange.end),
				workplaceIds: [workplaceId],
				calendarView: currentCalendarView,
			});
			tempEvents.push(...absenceEvents);
		}
		setEvents(tempEvents);
	};

	useEffectAsync(async () => {
		await handleLoadSchedule();
	}, [dateRange, currentCalendarView]);

	const handleDateRangeChange = (start: Moment, end: Moment) => {
		setDateRange({ start, end });
	};

	const handleChangeView = (view: CalendarView) => {
		setCurrentCalendarView(view);
	};

	const handleDateClick = (start: Moment, end: Moment, resourceId: string) => {
		setSelectedDate(start);
		setSelectedResourceId(resourceId);
		setContextMenuProps({
			isOpen: false,
		});
		setIsCreateModalOpen(true);
	};

	const handleEventClick = (data: EventClickArg) => {
		setSelectedDate(data.event.extendedProps.timeFrom);
		setSelectedServiceSchedule(data.event.extendedProps.serviceSchedule);
		setSelectedServiceScheduleItem(
			data.event.extendedProps.serviceScheduleItem,
		);
		setSelectedResourceId(data.event.extendedProps.resourceId);
		if ('clientX' in data.jsEvent && 'clientY' in data.jsEvent) {
			setContextMenuProps({
				isOpen: true,
				clientX: data.jsEvent.clientX as number,
				clientY: data.jsEvent.clientY as number,
			});
		}
	};

	const handleWipeStates = () => {
		setSelectedDate(undefined);
		setSelectedServiceSchedule(undefined);
		setSelectedServiceScheduleItem(undefined);
		setIsCreateModalOpen(false);
		setIsEditModalOpen(false);
	};

	const handleOnSuccess = async () => {
		handleWipeStates();
		await handleLoadSchedule();
		if (showNotification) {
			setIsOnlineOrderingNotificationOpen(true);
		}
	};

	const handleCloseCreateModal = () => {
		setIsCreateModalOpen(false);
	};

	const handleCloseUpdateModal = () => {
		setIsEditModalOpen(false);
	};

	const handleOpenRemoveSingle = () => {
		setContextMenuProps({ isOpen: false });
		setIsDeleteConfirmationOpen(true);
	};

	const handleOpenRemoveMultiple = () => {
		setContextMenuProps({ isOpen: false });
		setIsMultipleDeleteConfirmationOpen(true);
	};

	const handleDeleteServiceSchedule = () => {
		handleCloseUpdateModal();
		if (selectedServiceScheduleItem?.repeatable) {
			handleOpenRemoveMultiple();
		} else {
			handleOpenRemoveSingle();
		}
	};

	const handleRemoveEvent = async (excludeFuture: boolean) => {
		setIsLoading(true);
		setContextMenuProps({ isOpen: false });
		if (selectedServiceSchedule && selectedServiceScheduleItem) {
			const processRemove = async (obj?: {}) => {
				const params = obj ? new URLSearchParams(obj).toString() : '';

				await apiClient.delete(
					`/workplaces/${workplaceId}/services-schedule/${selectedServiceSchedule.id}/items/${selectedServiceScheduleItem.id}?${params}`,
				);

				await handleLoadSchedule();
			};

			if (excludeFuture) {
				const d = moment(
					`${moment(selectedDate).format('YYYY-MM-DD')}T${
						selectedServiceScheduleItem.timeFrom
					}`,
				).toISOString();
				await processRemove({
					excludeDay: d,
					excludeFuture,
				});
			} else {
				await processRemove();
			}
		}
		setIsLoading(false);
	};

	const handleRemove = async (multiple: boolean) => {
		await handleRemoveEvent(multiple);
		handleWipeStates();
		setIsDeleteConfirmationOpen(false);
		setIsMultipleDeleteConfirmationOpen(false);
	};

	const handleRenderEvent = (arg: EventContentArg) => {
		const event = arg.event;

		if (
			currentCalendarView === CalendarView.LIST_WEEK ||
			currentCalendarView === CalendarView.TIMELINE_DAY
		) {
			return event.title;
		}

		return (
			<Tooltip trigger={event.title}>{event.extendedProps.tooltipText}</Tooltip>
		);
	};

	const handlePrint = async () => {
		setIsLoadingPrint(true);
		try {
			const response = await apiClient.get(
				`workplaces/${workplaceId}/print/shift-report?dateFrom=${dateRange?.start?.format(
					'YYYY-MM-DD',
				)}&dateTo=${dateRange?.end?.add(-1, 'minutes')?.format('YYYY-MM-DD')}`,
			);
			if (response.status === 200 && response.data) {
				// @ts-ignore
				const printWindow = window.open('', '_blank');
				if (printWindow) {
					const document = printWindow.document;
					if (document) {
						document.open();
						document.write(response.data);
						document.close();
					}
				}
			} else {
				showUnexpectedError();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoadingPrint(false);
	};

	return (
		<div style={{ height: '100%', position: 'relative' }}>
			{isLoadingEvents || (isLoadingPrint && <OverlayLoading />)}
			<TimelineCalendar
				resources={timelineCalendarResources}
				events={events}
				onDateRangeChange={handleDateRangeChange}
				onDateClick={handleDateClick}
				onEventClick={handleEventClick}
				onViewChange={handleChangeView}
				resourcesLabel={workplaceName}
				renderEvent={handleRenderEvent}
				onPrint={handlePrint}
			/>
			{isCreateModalOpen && selectedDate && selectedResourceId && (
				<ServiceScheduleCreateModal
					isOpen
					onClose={handleCloseCreateModal}
					selectedDate={selectedDate}
					resourceId={selectedResourceId}
					onSuccess={handleOnSuccess}
				/>
			)}
			{isEditModalOpen &&
				selectedDate &&
				selectedServiceSchedule &&
				selectedServiceScheduleItem && (
					<ServiceScheduleUpdateModal
						isOpen
						selectedDate={selectedDate}
						serviceSchedule={selectedServiceSchedule}
						serviceScheduleItem={selectedServiceScheduleItem}
						onSuccess={handleOnSuccess}
						onClose={handleCloseUpdateModal}
						onDelete={handleDeleteServiceSchedule}
					/>
				)}
			{isMultipleDeleteConfirmationOpen && (
				<ConfirmationModal
					title='base.deleteSchedule'
					text={t(
						'component.schedule.deleteMultipleScheduleConfirmationMessage',
					)}
					isOpen
					onClose={() => setIsMultipleDeleteConfirmationOpen(false)}>
					<div className='d-flex flex-row' style={{ gap: '15px' }}>
						{isLoading ? (
							<Loading />
						) : (
							<>
								<Button
									onClick={() => handleRemove(false)}
									title={'base.deleteOnlyThisOne'}
									theme={Theme.GREEN}
									disabled={isLoading}
								/>
								<Button
									onClick={() => handleRemove(true)}
									title={'base.deleleAlsoFutures'}
									theme={Theme.BLUE}
									disabled={isLoading}
								/>
							</>
						)}
					</div>
				</ConfirmationModal>
			)}
			{isDeleteConfirmationOpen && (
				<ConfirmationModal
					title='base.deleteSchedule'
					text={t('component.schedule.deleteScheduleConfirmationMessage') ?? ''}
					isOpen={isDeleteConfirmationOpen}
					onClose={() => setIsDeleteConfirmationOpen(false)}>
					{isLoading ? (
						<Loading />
					) : (
						<Button
							onClick={() => handleRemove(false)}
							title={'base.shared.delete'}
							theme={Theme.GREEN}
						/>
					)}
				</ConfirmationModal>
			)}
			{contextMenuProps.isOpen &&
				selectedDate &&
				selectedServiceSchedule &&
				selectedServiceScheduleItem && (
					<ContextMenu
						params={contextMenuProps}
						onContextClose={() => setContextMenuProps({ isOpen: false })}>
						<LinkButton
							onClick={() => {
								setIsEditModalOpen(true);
								setContextMenuProps({ isOpen: false });
							}}
							title={'base.shared.edit'}
							theme={Theme.BLUE}
							firstUpper
						/>
						<LinkButton
							onClick={() => {
								setIsCreateModalOpen(true);
								setContextMenuProps({ isOpen: false });
							}}
							title={'component.workplaceScheduleCalendar.createNewSchedule'}
							theme={Theme.BLUE}
							firstUpper
						/>
						<div style={{ marginTop: '20px' }} />
						{selectedServiceScheduleItem.repeatable ? (
							<LinkButton
								theme={Theme.RED}
								title='base.deleteSchedule'
								onClick={handleOpenRemoveMultiple}
								firstUpper
							/>
						) : (
							<LinkButton
								theme={Theme.RED}
								title='base.deleteSchedule'
								onClick={handleOpenRemoveSingle}
								firstUpper
							/>
						)}
					</ContextMenu>
				)}
			{isOnlineOrderingNotificationOpen &&
				getShouldBeHintDisplayed('doNoShowOnlineOrderingNotificationModal') && (
					<OnlineOrderingNotificationModal
						isOpened
						onClose={() => setIsOnlineOrderingNotificationOpen(false)}
					/>
				)}
			{isInstructionModalOpen &&
				getShouldBeHintDisplayed(
					'doNoShowServiceScheduleInstructionsModal',
				) && (
					<ServiceScheduleInstructionsModal
						isOpened
						onClose={() => setIsInstructionModalOpen(false)}
					/>
				)}
		</div>
	);
};

export default ServiceScheduleCalendar;
